import React from 'react';

/**
 * Props for the ErrorFallback component
 */
export interface ErrorFallbackProps {
  error: Error;
  resetError?: () => void;
}

/**
 * Default error fallback UI (Turkish locale)
 */
export function DefaultErrorFallback({ error, resetError }: ErrorFallbackProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        minHeight: '200px',
        backgroundColor: '#fff1f0',
        border: '1px solid #ffa39e',
        borderRadius: '8px',
        margin: '1rem',
      }}
    >
      <h2 style={{ color: '#cf1322', marginBottom: '1rem' }}>
        Bir hata oluştu
      </h2>
      <p style={{ color: '#595959', marginBottom: '1rem', textAlign: 'center' }}>
        {error.message || 'Beklenmeyen bir hata oluştu. Lütfen sayfayı yenileyin.'}
      </p>
      {resetError && (
        <button
          onClick={resetError}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#1890ff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Tekrar Dene
        </button>
      )}
    </div>
  );
}

/**
 * Props for ErrorBoundary component
 */
export interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Reset key - when this changes, the error boundary resets */
  resetKey?: string | number;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Generic Error Boundary component
 *
 * For Sentry integration, wrap your app with Sentry.ErrorBoundary instead:
 *
 * @example
 * ```tsx
 * import * as Sentry from '@sentry/react';
 *
 * // Option 1: Use Sentry's built-in ErrorBoundary
 * <Sentry.ErrorBoundary fallback={<DefaultErrorFallback error={error} />}>
 *   <App />
 * </Sentry.ErrorBoundary>
 *
 * // Option 2: Use this ErrorBoundary with Sentry reporting
 * <ErrorBoundary
 *   onError={(error, errorInfo) => {
 *     Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
 *   }}
 * >
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    // Call onError callback if provided
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset error state when resetKey changes
    if (
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ hasError: false, error: null });
    }
  }

  resetError = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return (
        <FallbackComponent
          error={this.state.error}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component to wrap a component with ErrorBoundary
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
): React.FC<P> {
  const WrappedComponent: React.FC<P> = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
}
